import { useState } from "react";
import {
    Check, X, HelpCircle, ExternalLink, ChevronDown, AlertTriangle,
    ShieldAlert, Cookie, FileText, Link2, Copy, CheckCircle2, Info, MessageSquare, Globe,
} from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
import { ChatPanel } from "./ChatPanel";

const CAT_EXPLAIN = {
    "strictly-necessary": "Essential for the site to work, for example login or security. No consent is required.",
    functional: "Remembers preferences and choices such as language. Usually needs consent.",
    analytics: "Measures how visitors use the site. Needs prior consent under ePrivacy and PECR.",
    advertising: "Builds profiles for targeted advertising. Needs prior, freely given consent.",
};

const CONSENT_INFO = {
    "Consent banner / CMP present": "Whether a consent tool blocks non essential cookies until the visitor makes a choice, as required by ePrivacy and PECR.",
    "Reject all as prominent as accept all": "Refusing should be as easy and visible as accepting. A reject option hidden behind extra clicks is not valid consent.",
    "Granular, not pre-ticked consent": "Visitors should be able to accept some categories and refuse others, and nothing non essential should be ticked by default.",
    "GPC / DNT honoured": "Global Privacy Control and Do Not Track are browser opt out signals that a compliant site should respect.",
    "Cookie policy matches cookies set": "The cookie policy should list the cookies actually observed, with their purpose and duration.",
};

// Action-oriented CTA label per gap, based on the mapped service.
const actionLabel = (gap) => {
    const s = (gap.service || "").toLowerCase();
    if (s.includes("representative")) return "Appoint a rep";
    if (s.includes("dpo")) return "Appoint a DPO";
    if (s.includes("ai governance")) return "Learn about AI Governance";
    if (s.includes("project")) return "Plan the fix";
    return "Fix now";
};

const GRADE_HEX = { A: "#22AA78", B: "#364F6B", C: "#E0A100", D: "#D64545", F: "#D64545" };

const gradeColor = (g) => GRADE_HEX[g] || "#667085";

const STATUS_STYLE = {
    present: { bg: "bg-[#e7f6ef]", text: "text-[#1f8a5f]", label: "Present", Icon: Check },
    partial: { bg: "bg-[#fff5e0]", text: "text-amber", label: "Partial", Icon: AlertTriangle },
    missing: { bg: "bg-[#fdeaea]", text: "text-danger", label: "Missing", Icon: X },
    "not-required": { bg: "bg-aqua-tint", text: "text-asphalt", label: "Not required", Icon: Check },
    unknown: { bg: "bg-[#f1f3f5]", text: "text-muted", label: "Unknown", Icon: HelpCircle },
};

const SEV_STYLE = {
    high: "bg-[#fdeaea] text-danger border-danger/30",
    medium: "bg-[#fff5e0] text-amber border-amber/30",
    low: "bg-asphalt-tint text-asphalt border-line",
};

const CAT_STYLE = {
    "strictly-necessary": "bg-asphalt-tint text-asphalt",
    functional: "bg-[#e9f4ff] text-asphalt",
    analytics: "bg-[#fff5e0] text-amber",
    advertising: "bg-[#fdeaea] text-danger",
};

const GradeDial = ({ grade, size = 160 }) => {
    const color = gradeColor(grade);
    const stroke = size * 0.08;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const frac = { A: 0.95, B: 0.78, C: 0.6, D: 0.42, F: 0.2 }[grade] || 0.5;
    return (
        <div className="relative" style={{ width: size, height: size }} data-testid="overall-grade-dial">
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#E4E7EC" strokeWidth={stroke} />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
                    strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - frac)}
                    style={{ transition: "stroke-dashoffset 1s ease" }} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="font-bold" style={{ color, fontSize: size * 0.44 }}>{grade}</span>
            </div>
        </div>
    );
};

const GradeCard = ({ title, grade, headline, Icon }) => (
    <div className="flex-1 bg-white border border-line rounded-lg p-6 flex items-center gap-5" data-testid={`grade-card-${title.toLowerCase().replace(/\W+/g, "-")}`}>
        <div className="w-16 h-16 rounded-md flex items-center justify-center font-bold text-3xl shrink-0"
            style={{ backgroundColor: `${gradeColor(grade)}18`, color: gradeColor(grade) }}>
            {grade}
        </div>
        <div>
            <div className="flex items-center gap-2 text-muted text-sm font-medium">
                <Icon className="w-4 h-4" /> {title}
            </div>
            <p className="text-asphalt font-medium mt-1 text-base">{headline}</p>
        </div>
    </div>
);

const ConfidenceChip = ({ confidence }) => {
    if (!confidence) return null;
    const map = { high: "text-[#1f8a5f]", medium: "text-amber", low: "text-muted" };
    return <span className={`text-xs font-medium ${map[confidence] || "text-muted"}`}>{confidence} confidence</span>;
};

const PolicyRow = ({ check }) => {
    const [open, setOpen] = useState(false);
    const s = STATUS_STYLE[check.status] || STATUS_STYLE.unknown;
    const hasEvidence = check.evidence_quote || check.source_url;
    return (
        <>
            <tr className="border-t border-line align-top">
                <td className="py-3 pr-4 text-sm font-medium text-asphalt min-w-[220px]">{check.item}</td>
                <td className="py-3 pr-4">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
                        <s.Icon className="w-3.5 h-3.5" /> {s.label}
                    </span>
                </td>
                <td className="py-3 pr-4 text-sm text-muted min-w-[240px]">
                    {check.note}
                    <div className="flex items-center gap-3 mt-1">
                        <ConfidenceChip confidence={check.confidence} />
                        {hasEvidence && (
                            <button onClick={() => setOpen(!open)} className="text-xs font-medium text-asphalt hover:text-aqua flex items-center gap-1">
                                Evidence <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
                            </button>
                        )}
                    </div>
                </td>
            </tr>
            {open && (
                <tr className="bg-asphalt-tint/40">
                    <td colSpan={3} className="px-4 py-3">
                        {check.evidence_quote
                            ? <p className="text-sm text-asphalt italic border-l-2 border-aqua pl-3">"{check.evidence_quote}"</p>
                            : <p className="text-sm text-muted">No matching clause found in the policy. Absence can be confirmed at the source below.</p>}
                        {check.source_url && (
                            <a href={check.source_url} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-medium text-asphalt hover:text-aqua mt-2">
                                <Link2 className="w-3 h-3" /> Open policy source
                            </a>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
};

const InfoTip = ({ text }) => (
    <Tooltip>
        <TooltipTrigger asChild>
            <button type="button" className="text-muted hover:text-asphalt transition-colors align-middle" data-testid="info-tip">
                <Info className="w-3.5 h-3.5" />
            </button>
        </TooltipTrigger>
        <TooltipContent className="bg-asphalt text-white max-w-[260px] text-xs leading-relaxed">{text}</TooltipContent>
    </Tooltip>
);

const BoolRow = ({ label, value, goodWhenTrue = true, info }) => {
    let Icon = HelpCircle, cls = "text-muted", text = "Unknown";
    if (value === true) { Icon = goodWhenTrue ? Check : X; cls = goodWhenTrue ? "text-[#1f8a5f]" : "text-danger"; text = "Yes"; }
    else if (value === false) { Icon = goodWhenTrue ? X : Check; cls = goodWhenTrue ? "text-danger" : "text-[#1f8a5f]"; text = "No"; }
    return (
        <div className="flex items-center justify-between py-3 border-b border-line last:border-0">
            <span className="text-sm text-asphalt flex items-center gap-1.5">{label} {info && <InfoTip text={info} />}</span>
            <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${cls}`}>
                <Icon className="w-4 h-4" /> {text}
            </span>
        </div>
    );
};

const REGIME_STATUS = {
    present: { label: "Appointed", cls: "bg-[#e7f6ef] text-[#1f8a5f]", Icon: Check },
    "not-required": { label: "Not required", cls: "bg-aqua-tint text-asphalt", Icon: Check },
    missing: { label: "Required, not appointed", cls: "bg-[#fdeaea] text-danger", Icon: X },
    unknown: { label: "Verify", cls: "bg-[#f1f3f5] text-muted", Icon: HelpCircle },
};

const RegimeCard = ({ r }) => {
    const s = REGIME_STATUS[r.status] || REGIME_STATUS.unknown;
    const key = (r.key || r.label || "regime").toLowerCase();
    return (
        <div className="bg-white border border-line rounded-lg p-5 flex flex-col" data-testid={`regime-card-${key}`}>
            <div className="flex items-start justify-between gap-3">
                <h3 className="text-sm font-bold text-asphalt leading-snug">{r.regime}</h3>
                <span className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${s.cls}`}>
                    <s.Icon className="w-3.5 h-3.5" /> {s.label}
                </span>
            </div>
            {r.provider && (
                <p className="text-xs font-medium text-asphalt mt-2">Representative: {r.provider}</p>
            )}
            <p className="text-sm text-muted mt-2 flex-1">{r.detail}</p>
            {r.status === "missing" && r.service_url && (
                <a href={r.service_url} target="_blank" rel="noreferrer" data-testid={`regime-cta-${key}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-asphalt hover:text-aqua mt-3">
                    <ExternalLink className="w-3.5 h-3.5" /> Appoint a {r.label} representative
                </a>
            )}
        </div>
    );
};

const Section = ({ title, Icon, children }) => (
    <section className="mt-10 gdpr-fade-up">
        <h2 className="flex items-center gap-2 text-xl font-bold text-asphalt mb-4">
            <Icon className="w-5 h-5 text-aqua" /> {title}
        </h2>
        {children}
    </section>
);

export const ReportView = ({ data }) => {
    const [copied, setCopied] = useState(false);
    const [chatGap, setChatGap] = useState(null);
    const { meta, privacy_policy, cookies, gaps, scan_method, sources } = data;

    const copyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const genTime = meta.generated_at
        ? new Date(meta.generated_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })
        : "";

    const ppHeadline = { A: "Comprehensive disclosures", B: "Mostly complete", C: "Several gaps", D: "Major gaps", F: "Largely non compliant" }[privacy_policy.grade] || "";
    const ckHeadline = cookies.incomplete
        ? "Scan incomplete, grade withheld"
        : ({ A: "Consent handled well", B: "Minor issues", C: "Notable issues", D: "Serious issues", F: "Cookies before consent" }[cookies.grade] || "Consent not fully verified");

    const before = cookies.set_before_consent || [];
    const beforeKeys = new Set(before.map((c) => `${c.name}|${c.domain}`));

    const repCheck = (privacy_policy.checks || []).find((c) => Array.isArray(c.regimes) && c.regimes.length);
    const repRegimes = repCheck?.regimes || [];

    return (
      <TooltipProvider delayDuration={150}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
            {/* hero */}
            <div className="flex flex-col md:flex-row items-center gap-8 gdpr-fade-up">
                <GradeDial grade={meta.overall_grade} />
                <div className="flex-1 text-center md:text-left">
                    <p className="text-sm font-medium text-muted uppercase tracking-wide">Overall grade</p>
                    <h1 className="text-3xl sm:text-4xl font-bold text-asphalt mt-1" data-testid="report-company">{meta.company_name}</h1>
                    <a href={meta.site_url} target="_blank" rel="noreferrer" className="text-sm text-aqua hover:underline inline-flex items-center gap-1">
                        {meta.site_url} <ExternalLink className="w-3 h-3" />
                    </a>
                    <p className="text-base text-asphalt mt-3 max-w-2xl">{meta.summary}</p>
                    <div className="flex flex-wrap items-center gap-3 mt-4 justify-center md:justify-start">
                        <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${scan_method === "runtime" ? "bg-aqua-tint text-asphalt border-aqua/40" : "bg-asphalt-tint text-muted border-line"}`}>
                            Scan depth: {scan_method === "runtime" ? "runtime browser" : "markup detection (live scan did not complete)"}
                        </span>
                        {meta.cache_hit && (
                            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-asphalt-tint text-muted border border-line" data-testid="cache-badge">
                                Cached profile from {genTime}
                            </span>
                        )}
                        <button onClick={copyLink} data-testid="copy-link-btn"
                            className="text-xs font-medium px-3 py-1.5 rounded-md border border-asphalt text-asphalt hover:bg-asphalt-tint transition-colors inline-flex items-center gap-1.5">
                            {copied ? <><CheckCircle2 className="w-3.5 h-3.5 text-[#1f8a5f]" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy link</>}
                        </button>
                    </div>
                    {!meta.cache_hit && genTime && <p className="text-xs text-muted mt-2">Generated {genTime}</p>}
                </div>
            </div>

            {/* sub grades */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <GradeCard title="Privacy Policy" grade={privacy_policy.grade} headline={ppHeadline} Icon={FileText} />
                <GradeCard title="Cookie / Consent" grade={cookies.grade} headline={ckHeadline} Icon={Cookie} />
            </div>

            {/* representative requirements by regime */}
            {repRegimes.length > 0 && (
                <Section title="Representative requirements by regime (Article 27 / FADP 14)" Icon={Globe}>
                    <p className="text-sm text-muted mb-4 max-w-3xl">
                        Each regime is assessed independently. A representative named in one region is never cancelled out by an establishment carve-out in another.
                    </p>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="regime-grid">
                        {repRegimes.map((r) => <RegimeCard key={r.key || r.label} r={r} />)}
                    </div>
                </Section>
            )}

            {/* privacy checklist */}
            <Section title="Privacy policy: Article 13 / 14 disclosures" Icon={FileText}>
                {privacy_policy.url
                    ? <a href={privacy_policy.url} target="_blank" rel="noreferrer" className="text-sm text-aqua hover:underline inline-flex items-center gap-1 mb-3">
                        Policy found at {privacy_policy.url} <ExternalLink className="w-3 h-3" />
                    </a>
                    : <p className="text-sm text-danger mb-3">No privacy policy could be located on this site, so items are marked unknown.</p>}
                <div className="overflow-x-auto gdpr-scroll border border-line rounded-lg">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-asphalt-tint">
                                <th className="py-3 px-4 text-xs font-bold uppercase text-muted">Disclosure</th>
                                <th className="py-3 px-4 text-xs font-bold uppercase text-muted">Status</th>
                                <th className="py-3 px-4 text-xs font-bold uppercase text-muted">Note and evidence</th>
                            </tr>
                        </thead>
                        <tbody>
                            {privacy_policy.checks.filter((c) => !(Array.isArray(c.regimes) && c.regimes.length)).map((c) => <PolicyRow key={c.item} check={c} />)}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* cookie findings */}
            <Section title="Cookies and consent" Icon={Cookie}>
                {cookies.incomplete && (
                    <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber/40 bg-[#fff5e0] px-4 py-3" data-testid="cookie-incomplete-note">
                        <AlertTriangle className="w-4 h-4 text-amber mt-0.5 shrink-0" />
                        <p className="text-sm text-asphalt">{cookies.scan_note}</p>
                    </div>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white border border-line rounded-lg p-5">
                        <h3 className="text-sm font-bold text-asphalt mb-2">Consent setup</h3>
                        <BoolRow label="Consent banner / CMP present" value={cookies.banner.present} info={CONSENT_INFO["Consent banner / CMP present"]} />
                        <BoolRow label="Reject all as prominent as accept all" value={cookies.banner.reject_all_parity} info={CONSENT_INFO["Reject all as prominent as accept all"]} />
                        <BoolRow label="Granular, not pre-ticked consent" value={cookies.banner.granular} info={CONSENT_INFO["Granular, not pre-ticked consent"]} />
                        <BoolRow label="GPC / DNT honoured" value={cookies.gpc_honoured} info={CONSENT_INFO["GPC / DNT honoured"]} />
                        <BoolRow label="Cookie policy matches cookies set" value={cookies.cookie_policy_matches} info={CONSENT_INFO["Cookie policy matches cookies set"]} />
                        {cookies.banner.cmp && <p className="text-xs text-muted mt-3">Detected CMP: {cookies.banner.cmp}</p>}
                        {cookies.cookie_policy_url
                            ? <a href={cookies.cookie_policy_url} target="_blank" rel="noreferrer" className="text-xs text-aqua hover:underline inline-flex items-center gap-1 mt-2"><Link2 className="w-3 h-3" /> Cookie policy</a>
                            : <p className="text-xs text-danger mt-2">No cookie policy found.</p>}
                    </div>

                    <div className="bg-white border border-line rounded-lg p-5">
                        <h3 className="text-sm font-bold text-asphalt mb-2">Set before consent</h3>
                        {before.length === 0
                            ? <p className="text-sm text-muted">No analytics or advertising cookies observed before consent in this scan.</p>
                            : <p className="text-sm text-danger">{before.filter((c) => ["analytics", "advertising"].includes(c.category)).length} tracking cookie(s) fired before any consent. This is a core ePrivacy / PECR concern.</p>}
                        <div className="mt-3 space-y-1.5 max-h-48 overflow-y-auto gdpr-scroll">
                            {before.slice(0, 30).map((c, i) => {
                                const fired = ["analytics", "advertising"].includes(c.category);
                                return (
                                    <Tooltip key={`${c.name}|${c.domain}|${i}`}>
                                        <TooltipTrigger asChild>
                                            <div className="flex items-center justify-between text-xs cursor-help rounded px-1 -mx-1 hover:bg-asphalt-tint/60" data-testid={`before-row-${i}`}>
                                                <span className="font-medium text-asphalt truncate max-w-[55%]">{c.name || c.domain}</span>
                                                <span className={`px-2 py-0.5 rounded-full ${CAT_STYLE[c.category] || "bg-asphalt-tint text-muted"}`}>{c.category}</span>
                                            </div>
                                        </TooltipTrigger>
                                        <TooltipContent className="bg-asphalt text-white max-w-[280px] text-xs leading-relaxed">
                                            <span className="font-semibold">{c.name || "(unnamed cookie)"}</span> set by <span className="font-semibold">{c.domain}</span>.
                                            {" "}{CAT_EXPLAIN[c.category] || "Category unknown."}
                                            {" "}{fired ? "It was written before any consent, which non essential cookies must not be under ePrivacy and PECR." : "Observed on first load."}
                                        </TooltipContent>
                                    </Tooltip>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <h3 className="text-sm font-bold text-asphalt mt-6 mb-2">All trackers observed</h3>
                <div className="overflow-x-auto gdpr-scroll border border-line rounded-lg">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-asphalt-tint">
                                <th className="py-3 px-4 text-xs font-bold uppercase text-muted">Name</th>
                                <th className="py-3 px-4 text-xs font-bold uppercase text-muted">Setting domain</th>
                                <th className="py-3 px-4 text-xs font-bold uppercase text-muted">Category</th>
                                <th className="py-3 px-4 text-xs font-bold uppercase text-muted">Before consent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(cookies.all_trackers || []).length === 0 && (
                                <tr><td colSpan={4} className="py-4 px-4 text-sm text-muted">No trackers detected.</td></tr>
                            )}
                            {(cookies.all_trackers || []).map((t, i) => {
                                const flagged = beforeKeys.has(`${t.name}|${t.domain}`);
                                return (
                                    <Tooltip key={`${t.name}|${t.domain}|${i}`}>
                                        <TooltipTrigger asChild>
                                            <tr className="border-t border-line cursor-help hover:bg-asphalt-tint/50 transition-colors" data-testid={`tracker-row-${i}`}>
                                                <td className="py-2.5 px-4 text-sm font-medium text-asphalt">{t.name || "(unnamed)"}</td>
                                                <td className="py-2.5 px-4 text-sm text-muted break-all">{t.domain}</td>
                                                <td className="py-2.5 px-4"><span className={`px-2 py-0.5 rounded-full text-xs ${CAT_STYLE[t.category] || "bg-asphalt-tint text-muted"}`}>{t.category}</span></td>
                                                <td className="py-2.5 px-4">{flagged ? <span className="text-xs font-semibold text-danger inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Yes</span> : <span className="text-xs text-muted">No</span>}</td>
                                            </tr>
                                        </TooltipTrigger>
                                        <TooltipContent className="bg-asphalt text-white max-w-[300px] text-xs leading-relaxed">
                                            <span className="font-semibold">{t.name || "(unnamed)"}</span> from <span className="font-semibold">{t.domain}</span>.
                                            {" "}{CAT_EXPLAIN[t.category] || "Category unknown."}
                                            {" "}{flagged ? "This fired before consent, a core ePrivacy and PECR concern." : "Not observed before consent."}
                                        </TooltipContent>
                                    </Tooltip>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </Section>

            {/* gaps */}
            <Section title="Prioritised gaps and next steps" Icon={ShieldAlert}>
                {gaps.length === 0
                    ? <p className="text-sm text-muted">No prioritised gaps were identified in this indicative scan.</p>
                    : (
                        <div className="space-y-3">
                            {gaps.map((g, i) => (
                                <div key={`${g.service_url}|${g.issue.slice(0, 24)}`} className="bg-white border border-line rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`gap-item-${i}`}>
                                    <span className={`shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full border uppercase ${SEV_STYLE[g.severity]}`}>{g.severity}</span>
                                    <p className="flex-1 text-sm text-asphalt">{g.issue}</p>
                                    <button onClick={() => setChatGap(g)} data-testid={`gap-cta-${i}`}
                                        className="shrink-0 text-xs font-semibold px-4 py-2 rounded-md bg-aqua text-asphalt hover:brightness-95 transition-all inline-flex items-center gap-1.5">
                                        <MessageSquare className="w-3.5 h-3.5" /> {actionLabel(g)}
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
            </Section>

            {/* sources + legend */}
            <div className="mt-10 pt-6 border-t border-line">
                <div className="flex flex-wrap gap-4 items-center text-xs text-muted mb-4">
                    <span className="font-semibold text-asphalt">Legend:</span>
                    {Object.entries(STATUS_STYLE).map(([k, v]) => (
                        <span key={k} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${v.bg} ${v.text}`}><v.Icon className="w-3 h-3" /> {v.label}</span>
                    ))}
                </div>
                {sources && sources.length > 0 && (
                    <div className="text-xs text-muted">
                        <span className="font-semibold text-asphalt">Sources: </span>
                        {sources.map((s, i) => (
                            s.startsWith("http")
                                ? <a key={`${s}|${i}`} href={s} target="_blank" rel="noreferrer" className="text-aqua hover:underline mr-3">{s}</a>
                                : <span key={`${s}|${i}`} className="mr-3">{s}</span>
                        ))}
                    </div>
                )}
                <p className="text-xs text-muted mt-4 italic">{meta.disclaimer}</p>
            </div>
        </div>
        {chatGap && (
            <ChatPanel gap={chatGap} company={meta.company_name} overallGrade={meta.overall_grade} onClose={() => setChatGap(null)} />
        )}
      </TooltipProvider>
    );
};
